import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Facebook, Instagram, Music2, Sparkles, Calendar, Clock,
  Trash2, Edit2, Copy, CheckCircle, RefreshCw, Eye, Send,
  Hash, Image as ImageIcon, Video, FileText, ChevronDown, ChevronUp,
  TrendingUp, Play, Download, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', bgColor: '#1877F220' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', bgColor: '#E4405F20' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: '#000000', bgColor: '#ffffff20' }
];

const STATUS_STYLES = {
  draft: { bg: 'bg-neutral-500/20', text: 'text-neutral-400', label: 'Draft' },
  scheduled: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Scheduled' },
  published: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Published' }
};

function SocialMediaAdmin() {
  const [activePlatform, setActivePlatform] = useState('instagram');
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [showScheduler, setShowScheduler] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  const [regeneratingVideo, setRegeneratingVideo] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);

  useEffect(() => {
    loadData();
  }, [activePlatform]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [contentRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/social-media/content?platform=${activePlatform}`, { headers }),
        axios.get(`${API_URL}/api/social-media/stats`, { headers })
      ]);

      setContent(contentRes.data.content || []);
      setStats(statsRes.data.stats || {});
    } catch (error) {
      console.error('Failed to load social media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrendingTopics = async () => {
    setLoadingTrending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/social-media/trending-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrendingTopics(res.data.topics || []);
    } catch (error) {
      console.error('Failed to load trending topics:', error);
    } finally {
      setLoadingTrending(false);
    }
  };

  const regenerateVideo = async (contentId) => {
    setRegeneratingVideo(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/social-media/regenerate-video/${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video regenerated successfully!');
      loadData();
      if (selectedContent?.content_id === contentId) {
        setSelectedContent(prev => ({
          ...prev,
          video_url: res.data.video_url,
          video_status: 'generated'
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate video');
    } finally {
      setRegeneratingVideo(false);
    }
  };

  const generateContent = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/social-media/generate-content`,
        { platform: activePlatform, topic: topic || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${activePlatform} content generated!`);
      setTopic('');
      loadData();
      setSelectedContent({ ...res.data.content, content_id: res.data.content_id, platform: activePlatform });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const deleteContent = async (contentId) => {
    if (!window.confirm('Delete this content?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/social-media/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content deleted');
      loadData();
      if (selectedContent?.content_id === contentId) {
        setSelectedContent(null);
      }
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const scheduleContent = async (contentId) => {
    if (!scheduleDate) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/social-media/content/${contentId}/schedule?scheduled_at=${scheduleDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content scheduled!');
      setShowScheduler(false);
      setScheduleDate('');
      loadData();
    } catch (error) {
      toast.error('Failed to schedule content');
    }
  };

  const updateContent = async () => {
    if (!selectedContent) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/social-media/content/${selectedContent.content_id}`,
        {
          platform: selectedContent.platform,
          content_type: selectedContent.content_type || 'post',
          caption: selectedContent.caption,
          hashtags: selectedContent.hashtags || [],
          media_description: selectedContent.media_description,
          status: selectedContent.status || 'draft'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Content updated!');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const generateImageFromSuggestion = async (contentId, mediaDescription) => {
    if (!mediaDescription) {
      toast.error('No media suggestion available');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/social-media/generate-image`,
        {
          content_id: contentId,
          prompt: mediaDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.image_url) {
        toast.success('Image generated successfully!');
        // Update the selected content with the new image
        setSelectedContent(prev => ({
          ...prev,
          generated_image_url: response.data.image_url
        }));
        loadData();
      }
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentPlatform = PLATFORMS.find(p => p.id === activePlatform);
  const platformStats = stats[activePlatform] || { total: 0, drafts: 0, scheduled: 0, published: 0 };

  return (
    <div className="space-y-4 md:space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
          Social Media Content
        </h3>
      </div>

      {/* Platform Tabs */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="flex gap-2 min-w-max md:min-w-0">
          {PLATFORMS.map((platform) => {
            const Icon = platform.icon;
            const isActive = activePlatform === platform.id;
            return (
              <button
                key={platform.id}
                onClick={() => { setActivePlatform(platform.id); setSelectedContent(null); }}
                className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 md:py-3 rounded-xl border transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-current'
                    : 'border-white/10 hover:border-white/20'
                }`}
                style={{
                  backgroundColor: isActive ? platform.bgColor : 'transparent',
                  color: isActive ? platform.color : '#999',
                  borderColor: isActive ? platform.color : undefined
                }}
              >
                <Icon className="w-4 h-4 md:w-5 md:h-5" />
                <span className="font-medium text-sm md:text-base">{platform.name}</span>
                {stats[platform.id] && (
                  <span className="text-xs opacity-60">({stats[platform.id].total})</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="flex md:grid md:grid-cols-4 gap-2 md:gap-3 min-w-max md:min-w-0">
          <div className="bg-neutral-800/50 rounded-lg p-2 md:p-3 text-center w-[80px] md:w-auto flex-shrink-0 md:flex-shrink">
            <p className="text-xl md:text-2xl font-bold">{platformStats.total}</p>
            <p className="text-xs text-neutral-400">Total</p>
          </div>
          <div className="bg-neutral-800/50 rounded-lg p-2 md:p-3 text-center w-[80px] md:w-auto flex-shrink-0 md:flex-shrink">
            <p className="text-xl md:text-2xl font-bold text-neutral-400">{platformStats.drafts}</p>
            <p className="text-xs text-neutral-400">Drafts</p>
          </div>
          <div className="bg-neutral-800/50 rounded-lg p-2 md:p-3 text-center w-[80px] md:w-auto flex-shrink-0 md:flex-shrink">
            <p className="text-xl md:text-2xl font-bold text-yellow-400">{platformStats.scheduled}</p>
            <p className="text-xs text-neutral-400">Scheduled</p>
          </div>
          <div className="bg-neutral-800/50 rounded-lg p-2 md:p-3 text-center w-[80px] md:w-auto flex-shrink-0 md:flex-shrink">
            <p className="text-xl md:text-2xl font-bold text-green-400">{platformStats.published}</p>
            <p className="text-xs text-neutral-400">Published</p>
          </div>
        </div>
      </div>

      {/* AI Content Generator */}
      <div className="bg-neutral-800/50 rounded-xl p-4" style={{ borderLeft: `3px solid ${currentPlatform?.color}` }}>
        <h4 className="font-bold mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5" style={{ color: currentPlatform?.color }} />
          Generate {currentPlatform?.name} Content
          {activePlatform === 'tiktok' && (
            <span className="text-xs bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-1 rounded-full ml-2">
              + AI Video (Sora 2)
            </span>
          )}
        </h4>
        
        {/* Trending Topics for TikTok */}
        {activePlatform === 'tiktok' && (
          <div className="mb-3 p-3 bg-neutral-900/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-400 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-400" />
                Trending AI Topics
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={loadTrendingTopics}
                disabled={loadingTrending}
                className="text-xs"
              >
                {loadingTrending ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {trendingTopics.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {trendingTopics.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => setTopic(t)}
                    className="text-xs px-2 py-1 bg-green-500/10 text-green-400 rounded-full hover:bg-green-500/20 transition-colors truncate max-w-[200px]"
                    title={t}
                  >
                    {t.length > 30 ? t.substring(0, 30) + '...' : t}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-500">Click refresh to load trending topics or leave topic empty for auto-selection</p>
            )}
          </div>
        )}
        
        <div className="flex gap-3">
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={activePlatform === 'tiktok' 
              ? 'Topic for TikTok video (leave empty for AI to use trending topics)'
              : `Topic for ${currentPlatform?.name} post (or leave blank for AI suggestion)`}
            className="bg-neutral-900 border-white/10 flex-1"
          />
          <Button
            onClick={generateContent}
            disabled={generating}
            style={{ backgroundColor: currentPlatform?.color }}
            className="text-white hover:opacity-90"
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {activePlatform === 'tiktok' ? 'Generate Video' : 'Generate'}
          </Button>
        </div>
        {activePlatform === 'tiktok' && generating && (
          <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
            <Video className="w-3 h-3" />
            Generating video with Sora 2... This may take 2-5 minutes.
          </p>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Content List */}
        <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 overflow-hidden">
          <h4 className="font-bold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
            Content Library
          </h4>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : content.length === 0 ? (
            <p className="text-neutral-400 text-center py-8 text-sm">No content yet</p>
          ) : (
            <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0 md:overflow-y-auto md:max-h-[400px] scrollbar-hide">
              <div className="flex md:flex-col gap-3 md:gap-2 min-w-max md:min-w-0">
                {content.map((item) => {
                  const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.draft;
                  return (
                    <div
                      key={item.content_id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all w-[200px] md:w-auto flex-shrink-0 md:flex-shrink ${
                        selectedContent?.content_id === item.content_id
                          ? 'border-[#00F0FF] bg-[#00F0FF]/5'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      onClick={() => { setSelectedContent(item); setEditMode(false); }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm line-clamp-2">{item.caption?.substring(0, 60)}...</p>
                          <div className="flex items-center gap-1 md:gap-2 mt-2 flex-wrap">
                            <span className={`text-xs px-1.5 md:px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                              {statusStyle.label}
                            </span>
                            {item.platform === 'tiktok' && item.video_status === 'generated' && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] flex items-center gap-1">
                                <Video className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteContent(item.content_id); }}
                          className="text-neutral-500 hover:text-red-400 p-1"
                        >
                          <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Content Detail/Editor */}
        <div className="bg-neutral-800/50 rounded-xl p-3 md:p-4 overflow-hidden">
          {selectedContent ? (
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-sm md:text-base">Details</h4>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditMode(!editMode)} className="border-white/20">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowScheduler(!showScheduler)} className="border-yellow-500 text-yellow-400">
                    <Calendar className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Scheduler */}
              {showScheduler && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <label className="text-sm text-yellow-400 mb-2 block">Schedule for:</label>
                  <div className="flex gap-2">
                    <Input
                      type="datetime-local"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="bg-neutral-900 border-white/10 flex-1"
                    />
                    <Button onClick={() => scheduleContent(selectedContent.content_id)} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                      Schedule
                    </Button>
                  </div>
                </div>
              )}

              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-neutral-400">Caption</label>
                    <Textarea
                      value={selectedContent.caption || ''}
                      onChange={(e) => setSelectedContent({...selectedContent, caption: e.target.value})}
                      className="bg-neutral-900 border-white/10 mt-1"
                      rows={6}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-400">Hashtags (comma-separated)</label>
                    <Input
                      value={(selectedContent.hashtags || []).join(', ')}
                      onChange={(e) => setSelectedContent({...selectedContent, hashtags: e.target.value.split(',').map(t => t.trim())})}
                      className="bg-neutral-900 border-white/10 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-400">Media Description</label>
                    <Textarea
                      value={selectedContent.media_description || ''}
                      onChange={(e) => setSelectedContent({...selectedContent, media_description: e.target.value})}
                      className="bg-neutral-900 border-white/10 mt-1"
                      rows={2}
                    />
                  </div>
                  <Button onClick={updateContent} className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Caption */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-neutral-400">Caption</label>
                      <button onClick={() => copyToClipboard(selectedContent.caption)} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <div className="bg-neutral-900 p-3 rounded-lg text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {selectedContent.caption}
                    </div>
                  </div>

                  {/* Hashtags */}
                  {selectedContent.hashtags?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-neutral-400">Hashtags</label>
                        <button onClick={() => copyToClipboard(selectedContent.hashtags.map(t => `#${t}`).join(' '))} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Copy All
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {selectedContent.hashtags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Media Description */}
                  {selectedContent.media_description && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-neutral-400 mb-1 block">
                          <ImageIcon className="w-3 h-3 inline mr-1" />
                          Media Suggestion
                        </label>
                        <p className="text-sm text-neutral-300 bg-neutral-900 p-3 rounded-lg">
                          {selectedContent.media_description}
                        </p>
                      </div>
                      
                      {/* Generated Image Section */}
                      <div className="bg-neutral-900 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs text-neutral-400 flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            Generated Image
                          </label>
                          {selectedContent.generated_image_url && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              Ready
                            </span>
                          )}
                        </div>
                        
                        {selectedContent.generated_image_url ? (
                          <div className="space-y-2">
                            <img
                              src={selectedContent.generated_image_url.startsWith('http') 
                                ? selectedContent.generated_image_url 
                                : `${API_URL}${selectedContent.generated_image_url}`}
                              alt="Generated content"
                              className="w-full rounded-lg max-h-[300px] object-cover"
                            />
                            <div className="flex gap-2">
                              <a
                                href={selectedContent.generated_image_url.startsWith('http') 
                                  ? selectedContent.generated_image_url 
                                  : `${API_URL}${selectedContent.generated_image_url}`}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1"
                              >
                                <Button size="sm" variant="outline" className="w-full border-white/20">
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                              </a>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => generateImageFromSuggestion(selectedContent.content_id, selectedContent.media_description)}
                                disabled={generatingImage}
                                className="flex-1 border-white/30 text-white hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] hover:border-[#00F0FF]/50"
                              >
                                {generatingImage ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                    Regenerating...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Regenerate
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <ImageIcon className="w-8 h-8 text-neutral-600 mx-auto mb-2" />
                            <p className="text-xs text-neutral-500 mb-3">No image generated yet</p>
                            <Button
                              size="sm"
                              onClick={() => generateImageFromSuggestion(selectedContent.content_id, selectedContent.media_description)}
                              disabled={generatingImage}
                              className="bg-white text-black hover:bg-neutral-200"
                            >
                              {generatingImage ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-1" />
                                  Generate Image
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* TikTok Video Section */}
                  {selectedContent.platform === 'tiktok' && (
                    <div className="space-y-3">
                      {/* Video Status & Player */}
                      <div className="bg-neutral-900 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs text-neutral-400 flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            Generated Video
                          </label>
                          {selectedContent.video_status === 'generated' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              Ready
                            </span>
                          )}
                          {selectedContent.video_status === 'failed' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                              Failed
                            </span>
                          )}
                          {!selectedContent.video_status && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-500/20 text-neutral-400">
                              Not Generated
                            </span>
                          )}
                        </div>
                        
                        {selectedContent.video_url && selectedContent.video_status === 'generated' ? (
                          <div className="space-y-2">
                            <video
                              controls
                              className="w-full rounded-lg max-h-[300px] bg-black"
                              src={`${API_URL}${selectedContent.video_url}`}
                            >
                              Your browser does not support the video tag.
                            </video>
                            <div className="flex gap-2">
                              <a
                                href={`${API_URL}${selectedContent.video_url}`}
                                download
                                className="flex-1"
                              >
                                <Button size="sm" variant="outline" className="w-full border-white/20">
                                  <Download className="w-4 h-4 mr-2" />
                                  Download
                                </Button>
                              </a>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => regenerateVideo(selectedContent.content_id)}
                                disabled={regeneratingVideo}
                                className="border-white text-white"
                              >
                                {regeneratingVideo ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            {selectedContent.video_status === 'failed' ? (
                              <div className="space-y-2">
                                <AlertCircle className="w-8 h-8 mx-auto text-red-400" />
                                <p className="text-sm text-neutral-400">Video generation failed</p>
                                <Button
                                  size="sm"
                                  onClick={() => regenerateVideo(selectedContent.content_id)}
                                  disabled={regeneratingVideo}
                                  className="bg-white text-black hover:bg-[#00F0FF]/90"
                                >
                                  {regeneratingVideo ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-2" />
                                      Retry Generation
                                    </>
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Video className="w-8 h-8 mx-auto text-neutral-500" />
                                <p className="text-sm text-neutral-400">No video generated yet</p>
                                <Button
                                  size="sm"
                                  onClick={() => regenerateVideo(selectedContent.content_id)}
                                  disabled={regeneratingVideo}
                                  className="bg-white text-black hover:bg-[#00F0FF]/90"
                                >
                                  {regeneratingVideo ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="w-4 h-4 mr-2" />
                                      Generate Video
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Video Script */}
                      {selectedContent.video_script && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-neutral-400">Video Script</label>
                            <button onClick={() => copyToClipboard(selectedContent.video_script)} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1">
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>
                          <div className="bg-neutral-900 p-3 rounded-lg text-sm whitespace-pre-wrap max-h-24 overflow-y-auto">
                            {selectedContent.video_script}
                          </div>
                        </div>
                      )}

                      {/* Video Prompt */}
                      {selectedContent.video_prompt && (
                        <div>
                          <label className="text-xs text-neutral-400 mb-1 block">AI Video Prompt</label>
                          <div className="bg-neutral-900 p-3 rounded-lg text-xs text-neutral-400 max-h-20 overflow-y-auto">
                            {selectedContent.video_prompt}
                          </div>
                        </div>
                      )}

                      {/* Trending Topic Used */}
                      {selectedContent.trending_topic && (
                        <div className="flex items-center gap-2 text-sm text-green-400">
                          <TrendingUp className="w-4 h-4" />
                          Based on trending: {selectedContent.trending_topic}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tips */}
                  {selectedContent.engagement_tips && (
                    <div className="bg-white text-black/10 border border-white/30 rounded-lg p-3">
                      <label className="text-xs text-white mb-1 block">💡 Engagement Tip</label>
                      <p className="text-sm">{selectedContent.engagement_tips}</p>
                    </div>
                  )}

                  {/* Best Time */}
                  {selectedContent.best_posting_time && (
                    <div className="flex items-center gap-2 text-sm text-neutral-400">
                      <Clock className="w-4 h-4" />
                      Best time to post: {selectedContent.best_posting_time}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-neutral-400 py-8 md:py-12">
              <currentPlatform.icon className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 opacity-30" style={{ color: currentPlatform?.color }} />
              <p className="text-xs md:text-sm">Select content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SocialMediaAdmin;
